import { useState } from "react";
import { copy } from "../copy";
import { topicTitle } from "../domain/display";
import { readFindPrefs, writeFindPrefs } from "../domain/findPrefs";
import { topicsForAccount } from "../domain/selectors";
import { typeCovered } from "../domain/executors";
import { DIRECTION_PRESETS, evenWeights, setPlatformWeight, sortByRank, taskStatusLine, taskWeights, tasksForAccount } from "../domain/tasks";
import { useApp } from "../state/store";
import { DISTRIBUTION_PLATFORMS } from "../types";

const QUICK_COUNTS = [5, 10, 20];

export function Find() {
  return (
    <div className="stack">
      <FindForm intent="task" />
      <RecentFinds />
    </div>
  );
}

export function FindForm({ intent = "task", onDone }: { intent?: "task" | "prefs"; onDone?: () => void }) {
  const app = useApp();
  const stored = readFindPrefs();
  const [platforms, setPlatforms] = useState<string[]>(stored.platforms);
  const [weights, setWeights] = useState(() => stored.weights.length ? stored.weights : evenWeights(stored.platforms));
  const [brief, setBrief] = useState("");
  const [count, setCount] = useState(stored.count);
  const allOn = platforms.length === DISTRIBUTION_PLATFORMS.length;

  function choosePlatforms(next: string[]) {
    setPlatforms(next);
    setWeights(evenWeights(next));
  }

  function toggle(label: string) {
    choosePlatforms(platforms.includes(label) ? platforms.filter((item) => item !== label) : [...platforms, label]);
  }

  function submit() {
    if (intent === "prefs") {
      writeFindPrefs({ count, platforms, weights });
      app.notify(copy.prefsSaved);
      onDone?.();
      return;
    }
    app.createTask({
      type: "找选题",
      platforms,
      weights,
      direction: brief.trim(),
      count,
    });
    onDone?.();
  }

  return (
    <div className="stack">
      <section className="panel find-form">
        <div className="find-count">
          <span>{copy.find.count}</span>
          <div className="count-picks">
            {QUICK_COUNTS.map((value) => (
              <button key={value} type="button" className={count === value ? "button line" : "button ghost"} aria-pressed={count === value} onClick={() => setCount(value)}>
                {value}
              </button>
            ))}
            <input
              type="number"
              min={1}
              max={100}
              aria-label={copy.find.count}
              value={Number.isFinite(count) ? count : ""}
              onChange={(event) => setCount(event.target.value === "" ? 0 : Number(event.target.value))}
            />
          </div>
        </div>
        <fieldset>
          <legend>{copy.find.platforms}</legend>
          <label className="check-line">
            <input type="checkbox" checked={allOn} onChange={() => choosePlatforms(allOn ? [] : [...DISTRIBUTION_PLATFORMS])} />
            {copy.find.all}
          </label>
          <div className="choice-grid">
            {DISTRIBUTION_PLATFORMS.map((label) => (
              <label key={label} className="check-line">
                <input type="checkbox" checked={platforms.includes(label)} onChange={() => toggle(label)} />
                {label}
              </label>
            ))}
          </div>
          {weights.length ? (
            <div className="weight-list">
              {weights.map((item) => (
                <label key={item.platform} className="weight-row">
                  <span>{item.platform}</span>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    aria-label={`${item.platform} ${copy.find.weight}`}
                    value={item.weight}
                    onChange={(event) => setWeights((current) => setPlatformWeight(current, item.platform, Number(event.target.value)))}
                  />
                  <span className="weight-percent">
                    <input
                      type="number"
                      min={0}
                      max={100}
                      aria-label={`${item.platform} 百分比`}
                      value={item.weight}
                      onChange={(event) => setWeights((current) => setPlatformWeight(current, item.platform, Number(event.target.value)))}
                    />
                    <span>%</span>
                  </span>
                </label>
              ))}
              <p className="muted">{copy.find.sum}</p>
            </div>
          ) : null}
        </fieldset>
        {intent === "task" ? (
          <>
            <label>
              {copy.find.direction}
              <textarea value={brief} maxLength={400} rows={3} placeholder={copy.find.keyword} onChange={(event) => setBrief(event.target.value)} />
            </label>
            <div className="choice-grid">
              {DIRECTION_PRESETS.map((preset) => (
                <button key={preset} type="button" className={brief === preset ? "button line" : "button ghost"} aria-pressed={brief === preset} onClick={() => setBrief(preset)}>
                  {preset}
                </button>
              ))}
            </div>
          </>
        ) : null}
        <div className="find-submit">
          <button type="button" className={intent === "prefs" ? "button line" : "button primary"} onClick={submit} disabled={!platforms.length || count < 1 || count > 100}>
            {intent === "prefs" ? copy.savePrefs : copy.find.submit}
          </button>
        </div>
      </section>
    </div>
  );
}

function RecentFinds() {
  const app = useApp();
  const savedId = null as string | null;
  const tasks = tasksForAccount(app.state.tasks ?? [], app.accountId)
    .filter((task) => task.type === "找选题")
    .sort((left, right) => right.createdAt.localeCompare(left.createdAt))
    .slice(0, 8);
  const topics = topicsForAccount(app.state.topics, app.accountId);
  return (
      <section className="stack" aria-label={copy.find.recent}>
        <h2 className="section-label">{copy.find.recent}</h2>
        {tasks.length === 0 ? <p className="muted">{copy.find.recentEmpty}</p> : null}
        {tasks.map((task) => {
          const found = sortByRank(topics.filter((topic) => topic.sourceTaskId === task.id));
          const shares = taskWeights(task);
          return (
            <article key={task.id} id={`find-${task.id}`} className={task.id === savedId ? "panel find-card is-current" : "panel find-card"}>
              <header className="panel-head">
                <h3>{task.direction || "未写要求"}</h3>
                <span className="muted">{task.count} 条</span>
              </header>
              <p>{taskStatusLine(task, app.agentReady, typeCovered(app.state.executors ?? [], task.type))}</p>
              <p className="muted">{shares.map((item) => `${item.platform} ${item.weight}%`).join(" · ")}</p>
              {found.length ? (
                <ul className="find-hits">
                  {found.map((topic) => (
                    <li key={topic.id}>
                      <button type="button" className="text-link" onClick={() => app.openTopic(topic.id)}>
                        {topic.recommendRank != null ? <em className="rank-mark">{topic.recommendRank}</em> : null}
                        {topicTitle(topic)}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted">{copy.find.noResult}</p>
              )}
              {found.length ? (
                <button type="button" className="button line" onClick={() => app.navigate("inbox", `task=${encodeURIComponent(task.id)}`)}>
                  {copy.find.seeInbox}
                </button>
              ) : null}
            </article>
          );
        })}
      </section>
  );
}
